import React, { useCallback, useEffect, useState } from 'react';
import { milestoneApi } from '../services/api';
import type { Milestone, MilestoneMessage, MilestoneStartDecision } from '../types';
import { attestationRegistered } from '../utils/attestationRegistered';
import MilestoneAttestationModal from './MilestoneAttestationModal';

interface MessageAttestationDialogProps {
  milestoneId: string;
  contractId: string;
  contractNumber?: number;
  /** Closed, after an attestation or an objection or without one. */
  onClose: () => void;
}

/**
 * The stage's attestation opened from an attestation request in Meddelanden
 * (ClickUp 869fm3fy8): the same modal and the same calls as on the project's
 * timeline, so the customer attests or objects without leaving the
 * conversation.
 */
export function MessageAttestationDialog({ milestoneId, contractId, contractNumber, onClose }: MessageAttestationDialogProps) {
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  const [contractMilestones, setContractMilestones] = useState<Milestone[]>([]);
  const [messages, setMessages] = useState<MilestoneMessage[]>([]);

  const load = useCallback(async () => {
    const [stages, thread] = await Promise.all([
      milestoneApi.getMilestones(contractId),
      milestoneApi.getMessages(milestoneId),
    ]);
    setContractMilestones(stages);
    setMilestone(stages.find(stage => stage.id === milestoneId) ?? null);
    setMessages(thread);
  }, [contractId, milestoneId]);

  useEffect(() => {
    load().catch(() => onClose());
  }, [load, onClose]);

  const attest = async (
    action: 'approved' | 'rejected',
    bankidVerificationRef?: string,
    message?: string,
    milestoneStartDecisions?: MilestoneStartDecision[],
    files?: File[]
  ) => {
    let outcome;
    try {
      outcome = await milestoneApi.attestMilestone(milestoneId, { action, bankidVerificationRef, message, milestoneStartDecisions, files });
    } catch (error) {
      // A request that timed out may already be saved: check before reporting
      // a failure, as on the timeline (ClickUp 869fgx15f).
      const current = await milestoneApi.getMilestones(contractId).catch(() => [] as Milestone[]);
      if (!attestationRegistered(current.find(stage => stage.id === milestoneId), 'person', action)) throw error;
    }
    void load().catch(() => {});
    return outcome;
  };

  const postMessage = async (message: string, options: { context?: 'attestation_question' | 'attestation_request'; files?: File[] } = {}) => {
    await milestoneApi.postMessage(milestoneId, message, options);
    await load();
  };

  if (!milestone) return null;
  return (
    <MilestoneAttestationModal
      milestone={milestone}
      contractNumber={contractNumber}
      contractMilestones={contractMilestones}
      messages={messages}
      userRole="person"
      onAttest={attest}
      onPostMessage={postMessage}
      onClose={onClose}
    />
  );
}
